/**
 * Utility functions to apply trained mappings to new and existing data
 * 
 * This module provides functions to:
 * 1. Apply bank transaction categorization rules to new imports
 * 2. Apply POS sales item mappings to menu_performance data
 * 3. Apply COGS vendor ingredient mappings to invoice line items
 * 
 * These functions should be called after:
 * - New data is imported (auto sync or manual CSV upload)
 * - Training is completed and new rules/mappings are created
 */

import { supabase } from '@/integrations/supabase/client';
import { classifyTransaction, type TransactionToCategorize } from './transactionClassifier';
import { getPOSSalesItemMappings, type POSSalesItemMapping } from './posSalesItemMapping';
import { PLATFORMS } from '@/lib/platformConstants';
import { autoMapInvoiceLineItems } from './vendorInvoiceAutoMapper';
import { ensurePayrollClarificationNotification } from './payrollNotificationCreation';
import {
  createUnrecognizedExpenseNotificationIfNeeded,
  UNRECOGNIZED_CONFIDENCE_THRESHOLD,
} from './notificationCreation';
import { refreshDataGapNotifications } from './dataGapNotifications';

// ============================================================================
// Bank Transaction Categorization
// ============================================================================

export interface ApplyBankRulesResult {
  total: number;
  categorized: number;
  needsReview: number;
  uncategorized: number;
}

/**
 * Apply bank transaction categorization rules to uncategorized transactions
 * in the bank_transactions table
 */
export async function applyBankRulesToNewTransactions(
  companyId: string,
  accountId?: string,
  transactionIds?: string[]
): Promise<ApplyBankRulesResult> {
  // Build query for uncategorized transactions
  let query = supabase
    .from('bank_transactions')
    .select('id, transaction_date, amount, description, merchant_name, account_id, location_id, payroll_assignment_status')
    .eq('company_id', companyId)
    .is('category_type', null);

  if (accountId) {
    query = query.eq('account_id', accountId);
  }

  if (transactionIds && transactionIds.length > 0) {
    query = query.in('id', transactionIds);
  }

  const { data: transactions, error } = await query.order('transaction_date', { ascending: false });

  if (error) throw error;

  if (!transactions || transactions.length === 0) {
    return { total: 0, categorized: 0, needsReview: 0, uncategorized: 0 };
  }

  let categorized = 0;
  let needsReview = 0;

  // Process in batches of 50
  const batchSize = 50;
  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize);

    const classificationPromises = batch.map(async (tx) => {
      try {
        const classification = await classifyTransaction({
          id: tx.id,
          transaction_date: tx.transaction_date,
          amount: tx.amount,
          description: tx.description || '',
          merchant_name: tx.merchant_name,
          account_id: tx.account_id,
          company_id: companyId,
          location_id: tx.location_id,
        });

        if (classification.category_type) {
          // Update the transaction with the classification
          const updatePayload: Record<string, unknown> = {
            category_type: classification.category_type,
            subcategory: classification.subcategory,
            detail_source: classification.detail_source,
            classification_confidence: classification.confidence,
            classification_method: classification.method,
            auto_categorized: classification.confidence >= 0.8,
            needs_review: classification.needsReview || classification.confidence < 0.8,
            updated_at: new Date().toISOString(),
          };
          if (classification.subcategory === 'payroll' && tx.payroll_assignment_status !== 'assigned') {
            updatePayload.payroll_assignment_status = 'unassigned';
          }
          const { error: updateError } = await supabase
            .from('bank_transactions')
            .update(updatePayload)
            .eq('id', tx.id);

          if (!updateError) {
            categorized++;
            if (classification.needsReview || classification.confidence < UNRECOGNIZED_CONFIDENCE_THRESHOLD) {
              needsReview++;
              createUnrecognizedExpenseNotificationIfNeeded(companyId, tx.id, {
                merchant: tx.merchant_name ?? tx.description ?? null,
                amount: tx.amount,
                date: tx.transaction_date,
                source: null,
              }).catch((err) => console.error('Unrecognized expense notification creation failed:', err));
            }
            if (classification.subcategory === 'payroll' && updatePayload.payroll_assignment_status === 'unassigned') {
              ensurePayrollClarificationNotification(companyId, tx.id, {
                merchant: tx.merchant_name ?? tx.description ?? null,
                amount: tx.amount,
                date: tx.transaction_date,
                source: null,
              }).catch((err) => console.error('Payroll notification creation failed:', err));
            }
          }
        } else {
          // Still uncategorized after classification → unrecognized expense
          createUnrecognizedExpenseNotificationIfNeeded(companyId, tx.id, {
            merchant: tx.merchant_name ?? tx.description ?? null,
            amount: tx.amount,
            date: tx.transaction_date,
            source: null,
          }).catch((err) => console.error('Unrecognized expense notification creation failed:', err));
        }
      } catch (err) {
        console.error('Error classifying transaction:', tx.id, err);
      }
    });

    await Promise.all(classificationPromises);
  }

  await refreshDataGapNotifications(companyId).catch((err) =>
    console.error('refreshDataGapNotifications after classification failed:', err)
  );

  return {
    total: transactions.length,
    categorized,
    needsReview,
    uncategorized: transactions.length - categorized,
  };
}

/**
 * Apply bank statement categorization rules to uncategorized statements
 * in the bank_statements table (legacy table for manual CSV uploads)
 */
export async function applyBankRulesToStatements(
  companyId: string,
  accountId?: string
): Promise<ApplyBankRulesResult> {
  // Fetch active categorization rules
  const { data: rules, error: rulesError } = await supabase
    .from('bank_transaction_rules')
    .select('id, pattern, category_type, subcategory, detail_source, priority, confidence_score')
    .or(`company_id.is.null,company_id.eq.${companyId}`)
    .eq('is_active', true)
    .order('priority', { ascending: false });

  if (rulesError) throw rulesError;

  // Build query for uncategorized statements
  let query = supabase
    .from('bank_statements')
    .select('id, description')
    .eq('company_id', companyId)
    .is('category', null);

  if (accountId) {
    query = query.eq('bank_account_id', accountId);
  }

  const { data: statements, error } = await query;

  if (error) throw error;

  if (!statements || statements.length === 0) {
    return { total: 0, categorized: 0, needsReview: 0, uncategorized: 0 };
  }

  let categorized = 0;
  let needsReview = 0;

  // Apply rules to each statement
  for (const statement of statements) {
    if (!statement.description) continue;

    const matchingRule = rules?.find(rule =>
      statement.description!.toLowerCase().includes(rule.pattern.toLowerCase())
    );

    if (matchingRule) {
      const confidence = matchingRule.confidence_score || 85;
      const { error: updateError } = await supabase
        .from('bank_statements')
        .update({
          category: matchingRule.category_type,
          bucket: matchingRule.subcategory,
          auto_classified: true,
          confidence,
          matched_rule: matchingRule.id,
        })
        .eq('id', statement.id);

      if (!updateError) {
        categorized++;
        if (confidence < 80) {
          needsReview++;
        }
      }
    }
  }

  return {
    total: statements.length,
    categorized,
    needsReview,
    uncategorized: statements.length - categorized,
  };
}

// ============================================================================
// POS Sales Item Mappings
// ============================================================================

export interface ApplyPOSMappingsResult {
  total: number;
  mapped: number;
  unmapped: number;
}

/**
 * Apply POS sales item mappings to menu_performance data
 * This updates records that don't have mapped_item_id set
 */
export async function applyPOSMappingsToMenuPerformance(
  companyId: string,
  platform?: string
): Promise<ApplyPOSMappingsResult> {
  // Get all mappings for the company/platform
  const mappings = await getPOSSalesItemMappings(companyId, platform);

  if (mappings.length === 0) {
    return { total: 0, mapped: 0, unmapped: 0 };
  }

  // Create lookup maps
  const mappingByName = new Map<string, POSSalesItemMapping>();
  const mappingByIdentifier = new Map<string, POSSalesItemMapping>();

  mappings.forEach(m => {
    mappingByName.set(m.pos_item_name.toLowerCase(), m);
    mappingByIdentifier.set(m.pos_item_identifier.toLowerCase(), m);
  });

  // Build query for unmapped menu_performance records (table has source, not platform)
  let query = supabase
    .from('menu_performance')
    .select('id, item_name, source')
    .eq('company_id', companyId)
    .is('mapped_item_id', null);

  if (platform) {
    // Handle platform name variations
    const platformVariations = getPlatformVariations(platform);
    query = query.or(platformVariations.map(p => `source.ilike.%${p}%`).join(','));
  }

  const { data: records, error } = await query;

  if (error) throw error;

  if (!records || records.length === 0) {
    return { total: 0, mapped: 0, unmapped: 0 };
  }

  let mapped = 0;

  // Process in batches
  const batchSize = 100;
  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);
    const updates: Array<{ id: string; mapped_item_id: string; mapped_type: string }> = [];

    for (const record of batch) {
      const itemNameLower = record.item_name.toLowerCase();
      const mapping = mappingByName.get(itemNameLower) || mappingByIdentifier.get(itemNameLower);

      if (mapping && mapping.mapped_item_id) {
        updates.push({
          id: record.id,
          mapped_item_id: mapping.mapped_item_id,
          mapped_type: mapping.mapped_type,
        });
      }
    }

    // Batch update
    for (const update of updates) {
      const { error: updateError } = await supabase
        .from('menu_performance')
        .update({
          mapped_item_id: update.mapped_item_id,
          mapped_type: update.mapped_type,
        })
        .eq('id', update.id);

      if (!updateError) {
        mapped++;
      }
    }
  }

  return {
    total: records.length,
    mapped,
    unmapped: records.length - mapped,
  };
}

/**
 * Get platform name variations for filtering
 */
function getPlatformVariations(platform: string): string[] {
  const variations: Record<string, string[]> = {
    [PLATFORMS.TOAST]: ['toast', 'Toast'],
    [PLATFORMS.DOORDASH]: ['doordash', 'DoorDash', 'door dash'],
    ubereats: ['ubereats', 'uber eats', 'Uber Eats', 'UberEats', 'uber'],
    [PLATFORMS.UBER]: ['uber', 'ubereats', 'uber eats', 'Uber Eats', 'UberEats'],
  };

  return variations[platform.toLowerCase()] || [platform];
}

// ============================================================================
// COGS Vendor Invoice Mappings
// ============================================================================

export interface ApplyCOGSMappingsResult {
  total: number;
  autoMapped: number;
  flaggedForReview: number;
}

/**
 * Apply COGS vendor ingredient mappings to invoice line items
 */
export async function applyCOGSMappingsToInvoices(
  companyId: string,
  vendorId?: string
): Promise<ApplyCOGSMappingsResult> {
  // Get invoices that need processing
  let query = supabase
    .from('vendor_invoice_training')
    .select('id, vendor_id, line_items')
    .eq('company_id', companyId)
    .eq('is_processed', false);

  if (vendorId) {
    query = query.eq('vendor_id', vendorId);
  }

  const { data: invoices, error } = await query;

  if (error) throw error;

  if (!invoices || invoices.length === 0) {
    return { total: 0, autoMapped: 0, flaggedForReview: 0 };
  }

  let totalAutoMapped = 0;
  let totalFlagged = 0;

  for (const invoice of invoices) {
    const lineItems = invoice.line_items || [];
    if (lineItems.length === 0) continue;

    try {
      const { autoMapped, flaggedForReview } = await autoMapInvoiceLineItems(
        companyId,
        invoice.vendor_id,
        lineItems
      );

      totalAutoMapped += autoMapped.length;
      totalFlagged += flaggedForReview.length;

      // Mark invoice as processed if all items were auto-mapped
      if (autoMapped.length === lineItems.length) {
        await supabase
          .from('vendor_invoice_training')
          .update({ is_processed: true })
          .eq('id', invoice.id);
      }
    } catch (err) {
      console.error('Error processing invoice:', invoice.id, err);
    }
  }

  return {
    total: invoices.length,
    autoMapped: totalAutoMapped,
    flaggedForReview: totalFlagged,
  };
}

// ============================================================================
// Combined Apply All Mappings
// ============================================================================

export interface ApplyAllMappingsResult {
  bankTransactions: ApplyBankRulesResult;
  bankStatements: ApplyBankRulesResult;
  posMappings: ApplyPOSMappingsResult;
  cogsMappings: ApplyCOGSMappingsResult;
}

/**
 * Apply all trained mappings to new/unmapped data
 * This is useful to call after training is completed
 */
export async function applyAllTrainedMappings(
  companyId: string,
  options?: {
    accountId?: string;
    platform?: string;
    vendorId?: string;
  }
): Promise<ApplyAllMappingsResult> {
  const { accountId, platform, vendorId } = options || {};

  const [bankTransactions, bankStatements, posMappings, cogsMappings] = await Promise.all([
    applyBankRulesToNewTransactions(companyId, accountId).catch(err => {
      console.error('Error applying bank rules to transactions:', err);
      return { total: 0, categorized: 0, needsReview: 0, uncategorized: 0 };
    }),
    applyBankRulesToStatements(companyId, accountId).catch(err => {
      console.error('Error applying bank rules to statements:', err);
      return { total: 0, categorized: 0, needsReview: 0, uncategorized: 0 };
    }),
    applyPOSMappingsToMenuPerformance(companyId, platform).catch(err => {
      console.error('Error applying POS mappings:', err);
      return { total: 0, mapped: 0, unmapped: 0 };
    }),
    applyCOGSMappingsToInvoices(companyId, vendorId).catch(err => {
      console.error('Error applying COGS mappings:', err);
      return { total: 0, autoMapped: 0, flaggedForReview: 0 };
    }),
  ]);

  return {
    bankTransactions,
    bankStatements,
    posMappings,
    cogsMappings,
  };
}
